import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { Layout } from "../components/layout/Layout";
import { MediaGrid } from "../components/media/MediaGrid";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { Button } from "../components/common/Button";
import { AdBlock } from "../components/ads/AdBlock";
import { Pagination } from "../components/common/Pagination";
import { useQuery } from "@tanstack/react-query";
import { tmdbApi } from "../services/tmdb";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { SEOHead } from "../components/seo/SEOHead";
import { buildSEOTitle } from "../utils/seo";
import HighPerformanceBanner from '../components/ads/HighPerformanceBanner';
import HilltopAd from '../components/ads/HilltopAd';
import { Film, Tv, TrendingUp, Star, Clock, Filter, ChevronDown } from 'lucide-react';

const BrowseContainer = styled.div`
  display: flex;
  gap: var(--spacing-6);
  min-height: 100vh;
{{ ... }}
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
    padding: 0;
    width: 100%;
    overflow-x: hidden;
  }
`;

const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
  }
`;

const AdContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  margin: var(--spacing-4) 0;
  
  @media (max-width: 768px) {
    margin: var(--spacing-3) 0;
    width: 100%;
    
    &:first-child, &:last-child {
      display: none; /* Masquer les bannières latérales sur mobile */
    }
  }
`;

const Header = styled.div`
  margin-bottom: var(--spacing-4);

  @media (max-width: 768px) {
    margin-bottom: var(--spacing-3);
    padding: var(--spacing-3);
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: var(--spacing-3);
  color: var(--color-text-primary);

  @media (max-width: 768px) {
    font-size: 1.75rem;
    text-align: center;
    margin-bottom: var(--spacing-4);
    padding: 0 var(--spacing-2);
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: var(--spacing-3);
  margin-bottom: var(--spacing-5);
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: none; /* Masqué sur mobile, remplacé par MobileFilterContainer */
  }
`;

const MobileFilterContainer = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: var(--spacing-5);
  }
`;

const MobileTabsContainer = styled.div`
  display: flex;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: var(--spacing-3);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 10;
`;

const MobileTab = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 16px 12px;
  min-height: 56px;
  border: none;
  background: ${props => props.$isActive ? 'linear-gradient(135deg, var(--color-primary), #FFD700)' : 'transparent'};
  color: ${props => props.$isActive ? '#000' : 'var(--color-text-primary)'};
  font-weight: ${props => props.$isActive ? 'bold' : 'normal'};
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  cursor: pointer !important;
  user-select: none;
  -webkit-tap-highlight-color: rgba(255, 215, 0, 0.3);
  touch-action: manipulation;
  position: relative;
  z-index: 100;
  pointer-events: auto;
  
  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
  
  &:active {
    transform: scale(0.95);
    background: ${props => props.$isActive ? 'linear-gradient(135deg, var(--color-primary), #FFD700)' : 'rgba(255, 255, 255, 0.2)'};
  }
  
  &:hover {
    background: ${props => props.$isActive ? 'linear-gradient(135deg, var(--color-primary), #FFD700)' : 'rgba(255, 255, 255, 0.15)'};
  }
  
  svg {
    width: 20px;
    height: 20px;
    pointer-events: none;
  }
`;

const MobileFilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 18px;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 215, 0, 0.05));
  border: 2px solid rgba(255, 215, 0, 0.3);
  border-radius: 12px;
  color: var(--color-text-primary);
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: var(--spacing-3);
  cursor: pointer;
  touch-action: manipulation;
  min-height: 52px;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.98);
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 215, 0, 0.08));
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }
`;

const MobileFilterOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 2200; /* au-dessus des pubs mobiles */
  opacity: ${props => props.$isOpen ? 1 : 0};
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transition: opacity 0.25s ease;
`;

const MobileFilterDropdown = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 72px; /* au-dessus de la barre de navigation mobile */
  transform: translateY(${props => props.$isOpen ? '0%' : '100%'});
  transition: transform 0.3s ease;
  width: 100vw;
  max-height: 80vh;
  overflow: hidden;
  padding: var(--spacing-4);
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border-radius: 16px 16px 0 0;
  z-index: 2300; /* au-dessus de l'overlay */
  box-shadow: 0 -12px 30px rgba(0,0,0,0.45);
  border: 1px solid rgba(255, 215, 0, 0.15);
  /* Assure de l'espace au-dessus des barres système */
  padding-bottom: calc(var(--spacing-4) + env(safe-area-inset-bottom, 0px));
`;

const SheetHandle = styled.div`
  width: 42px;
  height: 4px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  margin: 0 auto var(--spacing-3);
`;

const SheetContent = styled.div`
  overflow-y: auto;
  /* Réduire pour laisser la place aux boutons + safe area */
  max-height: calc(80vh - 56px - 72px - env(safe-area-inset-bottom, 0px));
  padding-bottom: var(--spacing-3);
`;

const ActionRow = styled.div`
  display: flex;
  gap: var(--spacing-2);
  margin-top: var(--spacing-3);
  position: sticky;
  bottom: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.6));
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: var(--spacing-2);
  padding-bottom: calc(var(--spacing-2) + env(safe-area-inset-bottom, 0px));
  z-index: 1;
  backdrop-filter: blur(6px);
`;

const ApplyButton = styled(Button)`
  flex: 1;
  height: 50px;
`;

const ResetButton = styled(Button)`
  flex: 1;
  height: 50px;
`;

const SortContainer = styled.div`
  display: flex;
  gap: var(--spacing-3);
  margin-bottom: var(--spacing-5);
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 768px) {
    display: none !important; /* Masquer les boutons de tri sur mobile */
  }
`;

const ButtonBase = css<{ $isActive?: boolean }>`
  @media (max-width: 768px) {
    flex: 1;
    padding: 0.75rem;
    font-size: 1rem;
    transition: all 0.2s ease;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    white-space: nowrap;
    border-radius: 10px;
    
    ${props => props.$isActive ? `
      background: linear-gradient(135deg, var(--color-primary), #FFD700) !important;
      border: none !important;
      color: #000 !important;
      box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
      
      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(255, 215, 0, 0.4);
      }
    ` : `
      background: rgba(255, 255, 255, 0.1) !important;
      border: 1px solid rgba(255, 255, 255, 0.2) !important;
      color: var(--color-text-primary) !important;
      
      &:hover {
        background: rgba(255, 255, 255, 0.15) !important;
        border-color: rgba(255, 255, 255, 0.3) !important;
      }
    `}

    &:active {
      transform: translateY(0);
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const FilterButton = styled(Button)`
  font-size: 0.95rem;
  padding: 0.65rem 1.25rem;
  border-radius: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  ${ButtonBase}
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.75rem 1rem;
    height: 44px;
  }
`;

const SortButton = styled(Button)`
  font-size: 0.95rem;
  padding: 0.65rem 1.25rem;
  border-radius: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  ${ButtonBase}
`;

const GenreSelect = styled.select`
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 0.65rem 2.5rem 0.65rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  height: 42px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;

  &:hover {
    border-color: var(--color-primary);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  option {
    background: var(--color-background);
    color: var(--color-text-primary);
  }
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
    padding: 0.875rem 2.5rem 0.875rem 1.25rem;
    text-align: left;
    background-color: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(255, 255, 255, 0.15);
    min-height: 52px;
    width: 100%;
  }
`;

const AdWrapper = styled.div`
  border: 2px dashed red;
  padding: 20px;
  margin: 20px 0;
`;

const VideoWrapper = styled.div`
  border: 2px dashed blue;
  margin: 30px 0;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SortGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const GroupLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 0.5rem;
  white-space: nowrap;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: -0.5rem;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 20px;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Browse = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const mediaTypeParam = searchParams.get("type") || "movie";
  const mediaType = mediaTypeParam === "tv" ? "tv" : "movie";
  const selectedGenre = searchParams.get("genre") || "";
  const searchQuery = searchParams.get("q") || ""; // Paramètre de recherche
  const [sortBy, setSortBy] = useState(
    mediaType === "movie" ? "popularity.desc" : "popularity.desc"
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get("page") || "1")
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // Etats temporaires pour la feuille mobile
  const [tempSortBy, setTempSortBy] = useState(sortBy);
  const [tempGenre, setTempGenre] = useState(selectedGenre);
  
  // Détection de l'écran mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Syncer les valeurs temporaires à l'ouverture de la feuille
  useEffect(() => {
    if (isFilterOpen) {
      setTempSortBy(sortBy);
      setTempGenre(selectedGenre);
    }
  }, [isFilterOpen, sortBy, selectedGenre]);

  const applyMobileFilters = () => {
    setSortBy(tempSortBy);
    setSearchParams(prev => {
      if (tempGenre) prev.set('genre', tempGenre);
      else prev.delete('genre');
      return prev;
    });
    setCurrentPage(1);
    setIsFilterOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetMobileFilters = () => {
    setTempGenre("");
    setTempSortBy("popularity.desc");
  };

  const { data: genres } = useQuery({
    queryKey: ["genres", mediaType],
    queryFn: () => tmdbApi.getGenres(mediaType),
  });

  const handleMediaTypeChange = (type: "movie" | "tv") => {
    console.log('📺 Changement de type média:', type);
    // Mettre à jour le type de média dans les paramètres d'URL
    setSearchParams(prev => {
      prev.set("type", type);
      // Réinitialiser le genre quand on change de type de média
      prev.delete("genre");
      return prev;
    });
    // Réinitialiser la page
    setCurrentPage(1);
  };

  const handleMovieClick = () => {
    handleMediaTypeChange("movie");
  };

  const handleTvClick = () => {
    handleMediaTypeChange("tv");
  };

  // Requête pour la recherche
  const { data: searchData, isLoading: isSearchLoading } = useQuery({
    queryKey: ["search", searchQuery, currentPage],
    queryFn: async () => {
      const results = await tmdbApi.search(searchQuery, currentPage);
      
      // Trier les résultats par date de sortie (du plus récent au plus ancien)
      if (results?.results) {
        results.results.sort((a: any, b: any) => {
          const dateA = a.release_date || a.first_air_date || '0000-00-00';
          const dateB = b.release_date || b.first_air_date || '0000-00-00';
          return dateB.localeCompare(dateA); // Ordre décroissant (plus récent en premier)
        });
      }
      
      return results;
    },
    enabled: searchQuery.length > 0,
  });

  // Requête pour la découverte (films/séries)
  const { data: mediaData, isLoading: isDiscoverLoading } = useQuery({
    queryKey: ["discover", mediaType, currentPage, sortBy, selectedGenre],
    queryFn: () =>
      tmdbApi.getDiscover(mediaType, currentPage, sortBy, selectedGenre ? parseInt(selectedGenre) : undefined),
    enabled: !searchQuery, // Désactiver si recherche active
  });

  // Déterminer quelle donnée afficher
  const isLoading = searchQuery ? isSearchLoading : isDiscoverLoading;
  const data = searchQuery ? searchData : mediaData;

  const handleGenreChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (value) {
      setSearchParams(prev => {
        prev.set("genre", value);
        return prev;
      });
    } else {
      setSearchParams(prev => {
        prev.delete("genre");
        return prev;
      });
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getSortButtons = () => {
    return (
      <>
        <SortButton
          variant={sortBy === "popularity.desc" ? "primary" : "secondary"}
          icon={<TrendingUp size={16} />}
          onClick={() => setSortBy("popularity.desc")}
          $isActive={sortBy === "popularity.desc"}
        >
          Populaires
        </SortButton>
        <SortButton
          variant={sortBy === "vote_average.desc" ? "primary" : "secondary"}
          icon={<Star size={16} />}
          onClick={() => setSortBy("vote_average.desc")}
          $isActive={sortBy === "vote_average.desc"}
        >
          {mediaType === "movie" ? "Mieux notés" : "Mieux notées"}
        </SortButton>
        <SortButton
          variant={
            (mediaType === "movie" ? sortBy === "release_date.desc" : sortBy === "first_air_date.desc")
              ? "primary"
              : "secondary"
          }
          icon={<Clock size={16} />}
          onClick={() =>
            setSortBy(mediaType === "movie" ? "release_date.desc" : "first_air_date.desc")
          }
          $isActive={
            mediaType === "movie"
              ? sortBy === "release_date.desc"
              : sortBy === "first_air_date.desc"
          }
        >
          {mediaType === "movie" ? "Récents" : "Récentes"}
        </SortButton>
      </>
    );
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <Layout>
      {(() => {
        const resultCount = data?.results?.length || 0;
        const genreName = selectedGenre && genres?.genres?.find((g: { id: number; name: string }) => g.id === parseInt(selectedGenre))?.name;
        const seoTitle = searchQuery
          ? buildSEOTitle({
              baseTitle: searchQuery,
              prefix: 'Recherche:',
              hook: resultCount > 0 ? `${resultCount} résultat${resultCount > 1 ? 's' : ''}` : 'Aucun résultat',
            })
          : buildSEOTitle({
              baseTitle: mediaType === 'movie' ? 'Films à voir' : 'Séries à voir',
              prefix: 'Guide',
              hook: genreName ? `Genre: ${genreName}` : 'Tendances, mieux notés et récents',
            });
        const description = searchQuery
          ? `Parcourez les résultats pour "${searchQuery}" sur Cinédelux. Filtres, tri et pagination disponibles.`
          : `Découvrez ${mediaType === 'movie' ? 'les meilleurs films' : 'les meilleures séries'} par popularité, note et date de sortie${genreName ? ` – Genre: ${genreName}` : ''}.`;
        return (
          <SEOHead
            title={seoTitle}
            description={description}
            type="website"
          />
        );
      })()}
      <BrowseContainer>
        <AdContainer>
          <AdBlock format="banner" />
        </AdContainer>
        <MainContent>
          {/* Publicité HighPerformance */}
          <HighPerformanceBanner 
            adKey="2154f7f7df439957dfc24240cd37dd3c"
            width={728}
            height={90}
            id="hp-banner-browse"
            hideOnMobile
          />
          
          {/* Publicité HilltopAds */}
          <HilltopAd id="hilltop-browse-1" />
          
          <Header>
            <Title>{searchQuery ? `Résultats pour "${searchQuery}"` : 'Explorer'}</Title>
            
            {/* Version desktop des filtres - masqué en mode recherche */}
            {!searchQuery && <FilterContainer>
              <FilterGroup>
                <GroupLabel>Type</GroupLabel>
                <FilterButton
                  variant={mediaType === "movie" ? "primary" : "secondary"}
                  icon={<Film size={16} />}
                  onClick={() => handleMediaTypeChange("movie")}
                  $isActive={mediaType === "movie"}
                >
                  Films
                </FilterButton>
                <FilterButton
                  variant={mediaType === "tv" ? "primary" : "secondary"}
                  icon={<Tv size={16} />}
                  onClick={() => handleMediaTypeChange("tv")}
                  $isActive={mediaType === "tv"}
                >
                  Séries
                </FilterButton>
              </FilterGroup>
              
              <FilterGroup>
                <GroupLabel>Genre</GroupLabel>
                <GenreSelect 
                  value={selectedGenre || ""}
                  onChange={handleGenreChange}
                >
                  <option value="">Tous les genres</option>
                  {genres?.genres?.map((genre: { id: number; name: string }) => (
                    <option key={genre.id} value={genre.id}>
                      {genre.name}
                    </option>
                  ))}
                </GenreSelect>
              </FilterGroup>
            </FilterContainer>}
            
            {/* Version mobile des filtres - masqué en mode recherche */}
            {!searchQuery && <MobileFilterContainer>
              {/* Onglets Films/Séries */}
              <MobileTabsContainer>
                <MobileTab 
                  $isActive={mediaType === "movie"}
                  onClick={handleMovieClick}
                  type="button"
                  aria-label="Afficher les films"
                >
                  <Film size={20} />
                  Films
                </MobileTab>
                <MobileTab 
                  $isActive={mediaType === "tv"}
                  onClick={handleTvClick}
                  type="button"
                  aria-label="Afficher les séries"
                >
                  <Tv size={20} />
                  Séries
                </MobileTab>
              </MobileTabsContainer>
              
              {/* Overlay pour le dropdown */}
              <MobileFilterOverlay $isOpen={isFilterOpen} onClick={() => setIsFilterOpen(false)} />
              
              {/* Bouton Filtres avec dropdown */}
              <MobileFilterButton 
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                aria-expanded={isFilterOpen}
                style={{ fontWeight: 600, fontSize: '1.1rem', border: '2px solid var(--color-primary)' }}
              >
                <span><Filter size={18} /> Filtres et tri</span>
                <ChevronDown size={18} />
              </MobileFilterButton>
              
              {/* Bottom sheet de filtres */}
              <MobileFilterDropdown $isOpen={isFilterOpen}>
                <SheetHandle />
                <SheetContent>
                  <div style={{ marginBottom: 'var(--spacing-3)' }}>
                    <label style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontSize: '1rem', fontWeight: 500 }}>Genre :</label>
                    <GenreSelect 
                      value={tempGenre || ""}
                      onChange={(e) => setTempGenre(e.target.value)}
                      style={{ width: '100%', height: '44px', fontSize: '1.1rem' }}
                    >
                      <option value="">Tous les genres</option>
                      {genres?.genres?.map((genre: { id: number; name: string }) => (
                        <option key={genre.id} value={genre.id}>
                          {genre.name}
                        </option>
                      ))}
                    </GenreSelect>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontSize: '1rem', fontWeight: 500 }}>Trier par :</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
                      <SortButton
                        variant={tempSortBy === "popularity.desc" ? "primary" : "secondary"}
                        icon={<TrendingUp size={18} />}
                        onClick={() => setTempSortBy("popularity.desc")}
                        $isActive={tempSortBy === "popularity.desc"}
                        style={{ width: '100%', justifyContent: 'flex-start', fontSize: '1.1rem' }}
                      >
                        Populaires
                      </SortButton>
                      <SortButton
                        variant={tempSortBy === "vote_average.desc" ? "primary" : "secondary"}
                        icon={<Star size={18} />}
                        onClick={() => setTempSortBy("vote_average.desc")}
                        $isActive={tempSortBy === "vote_average.desc"}
                        style={{ width: '100%', justifyContent: 'flex-start', fontSize: '1.1rem' }}
                      >
                        {mediaType === "movie" ? "Mieux notés" : "Mieux notées"}
                      </SortButton>
                      <SortButton
                        variant={
                          (mediaType === "movie" ? tempSortBy === "release_date.desc" : tempSortBy === "first_air_date.desc")
                            ? "primary"
                            : "secondary"
                        }
                        icon={<Clock size={18} />}
                        onClick={() =>
                          setTempSortBy(mediaType === "movie" ? "release_date.desc" : "first_air_date.desc")
                        }
                        $isActive={
                          mediaType === "movie"
                            ? tempSortBy === "release_date.desc"
                            : tempSortBy === "first_air_date.desc"
                        }
                        style={{ width: '100%', justifyContent: 'flex-start', fontSize: '1.1rem' }}
                      >
                        {mediaType === "movie" ? "Récents" : "Récentes"}
                      </SortButton>
                    </div>
                  </div>
                </SheetContent>
                <ActionRow>
                  <ResetButton variant="secondary" onClick={resetMobileFilters}>Réinitialiser</ResetButton>
                  <ApplyButton variant="primary" onClick={applyMobileFilters}>Appliquer</ApplyButton>
                </ActionRow>
              </MobileFilterDropdown>
            </MobileFilterContainer>}

            {/* Boutons de tri pour desktop - masqué en mode recherche */}
            {!searchQuery && <SortContainer>
              <SortGroup>
                <GroupLabel>Trier par</GroupLabel>
                {getSortButtons()}
              </SortGroup>
            </SortContainer>}
          </Header>

          {data?.results && <MediaGrid items={data.results} type={mediaType} />}

          <AdContainer>
            <AdBlock format="native" />
          </AdContainer>

          {data?.total_pages && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.min(data.total_pages, 500)}
              onPageChange={handlePageChange}
            />
          )}
        </MainContent>
        <AdContainer>
          <AdBlock format="banner" />
        </AdContainer>
      </BrowseContainer>
      
      {/* Publicité HighPerformance en bas de page - en dehors du flex container */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
        <HighPerformanceBanner 
          adKey="2154f7f7df439957dfc24240cd37dd3c"
          width={728}
          height={90}
          id="hp-banner-browse-bottom"
        />
      </div>
    </Layout>
  );
};

export default Browse;
